import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import { useAuthStore } from '@/store/authStore';
import { usePlanStore } from '@/store/planStore';
import { useThemeStore } from '@/store/themeStore';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { commandRegistry } from '@/commands/registry';
import type { CommandContext } from '@/commands/types';
import { ExportMenu } from '@/ui/ExportMenu';
import { ImportDialog } from '@/ui/ImportDialog';
import { ShareDialog } from '@/ui/ShareDialog';
import { LayoutPicker } from '@/ui/LayoutPicker';
import type { MindMapDocument } from '@/model/document';

function getContext(): CommandContext {
  return {
    doc: useDocumentStore.getState().mapDoc,
    editorState: useEditorStore.getState(),
  };
}

function ToolbarButton({
  label,
  commandId,
  shortcut,
  compact,
  tooltip,
}: {
  label: React.ReactNode;
  commandId: string;
  shortcut?: string;
  compact?: boolean;
  tooltip?: string;
}) {
  const titleText = tooltip ?? (typeof label === 'string' ? label : '');
  return (
    <button
      onClick={() => commandRegistry.execute(commandId, getContext())}
      title={shortcut ? `${titleText} (${shortcut})` : titleText}
      style={{
        background: 'var(--bg-surface-raised)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-strong)',
        borderRadius: 6,
        padding: compact ? '6px 8px' : '6px 12px',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontFamily: 'var(--font-family)',
      }}
    >
      {label}
    </button>
  );
}

function MenuItem({ onClick, children, borderTop }: { onClick: () => void; children: React.ReactNode; borderTop?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: 'none',
        border: 'none',
        borderTop: borderTop ? '1px solid var(--border-subtle)' : 'none',
        color: 'var(--text-primary)',
        padding: '8px 12px',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontFamily: 'var(--font-family)',
      }}
    >
      {children}
    </button>
  );
}

interface ToolbarProps {
  onImported?: (mapDoc: MindMapDocument, mapId: string) => void;
  mapId?: string | null;
  onOpenAccountSettings?: () => void;
  onOpenPricing?: () => void;
}

export function Toolbar({ onImported, mapId, onOpenAccountSettings, onOpenPricing }: ToolbarProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const visible = useEditorStore((s) => s.toolbarVisible);
  const zoom = useEditorStore((s) => s.zoom);
  const meta = useDocumentStore((s) => s.meta);
  const logout = useAuthStore((s) => s.logout);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authUser = useAuthStore((s) => s.user);
  const currentPlan = usePlanStore((s) => s.plan);
  const appTheme = useThemeStore((s) => s.appTheme);
  const toggleAppTheme = useThemeStore((s) => s.toggleAppTheme);
  const [shareOpen, setShareOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mapDoc = useDocumentStore((s) => s.mapDoc);
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const multiSelectMode = useEditorStore((s) => s.multiSelectMode);
  const toggleMultiSelect = useEditorStore((s) => s.toggleMultiSelect);
  const nodes = useDocumentStore((s) => s.nodes);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  // Determine if selected node has children (for showing collapse button) and its collapsed state
  const selectedNodeId = selectedNodeIds[0];
  const selectedNode = selectedNodeId ? nodes.get(selectedNodeId) : undefined;
  const hasChildren = selectedNode != null && selectedNode.childIds.length > 0;
  const isCollapsed = selectedNode?.collapsed ?? false;

  if (!visible) return null;

  const undoIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
  const redoIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
    </svg>
  );

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 'var(--toolbar-height)',
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 4 : 8,
        padding: isMobile ? '4px 8px' : '8px 16px',
        background: 'var(--bg-app)',
        borderBottom: '1px solid var(--border-subtle)',
        zIndex: 10,
        boxSizing: 'border-box',
      }}
    >
      {editingTitle ? (
        <input
          ref={titleInputRef}
          defaultValue={meta.title}
          autoFocus
          onBlur={(e) => {
            const val = e.currentTarget.value.trim();
            if (val && val !== meta.title) mapDoc.setTitle(val);
            setEditingTitle(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const val = e.currentTarget.value.trim();
              if (val && val !== meta.title) mapDoc.setTitle(val);
              setEditingTitle(false);
            } else if (e.key === 'Escape') {
              setEditingTitle(false);
            }
          }}
          style={{
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-focus)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            fontFamily: 'var(--font-family)',
            padding: '2px 8px',
            marginRight: isMobile ? 8 : 16,
            outline: 'none',
            width: isMobile ? 120 : 200,
          }}
        />
      ) : (
        <>
          <span
            onDoubleClick={() => setEditingTitle(true)}
            title={t('toolbar.doubleClickRename')}
            style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              fontFamily: 'var(--font-family)',
              cursor: 'text',
              maxWidth: isMobile ? 120 : undefined,
              overflow: isMobile ? 'hidden' : undefined,
              textOverflow: isMobile ? 'ellipsis' : undefined,
              whiteSpace: isMobile ? 'nowrap' : undefined,
            }}
          >
            {meta.title}
          </span>
          <button
            onClick={() => setEditingTitle(true)}
            title={t('toolbar.renameMap')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              padding: '2px 4px',
              marginRight: isMobile ? 4 : 12,
            }}
          >
            &#9998;
          </button>
        </>
      )}

      <ToolbarButton
        label={undoIcon}
        tooltip={t('toolbar.undo')}
        commandId="edit.undo"
        shortcut="Ctrl+Z"
      />
      <ToolbarButton
        label={redoIcon}
        tooltip={t('toolbar.redo')}
        commandId="edit.redo"
        shortcut="Ctrl+Y"
      />

      {hasChildren && (
        <button
          onClick={() => commandRegistry.execute('node.toggleCollapse', getContext())}
          title={isCollapsed ? t('toolbar.expand') : t('toolbar.collapse')}
          style={{
            background: 'var(--bg-surface-raised)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            padding: '6px 8px',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {isCollapsed ? (
              /* Collapsed: chevron-right with vertical lines to suggest expanding */
              <>
                <polyline points="9 6 15 12 9 18" />
                <line x1="4" y1="6" x2="4" y2="18" />
                <line x1="20" y1="6" x2="20" y2="18" />
              </>
            ) : (
              /* Expanded: chevron-down with horizontal lines to suggest collapsing */
              <>
                <polyline points="6 9 12 15 18 9" />
                <line x1="6" y1="4" x2="18" y2="4" />
                <line x1="6" y1="20" x2="18" y2="20" />
              </>
            )}
          </svg>
        </button>
      )}

      {selectedNodeIds.length > 0 && (
        <button
          onClick={toggleMultiSelect}
          title={t('toolbar.multiSelect')}
          style={{
            background: multiSelectMode ? 'var(--color-primary)' : 'var(--bg-surface-raised)',
            color: multiSelectMode ? 'var(--text-on-primary)' : 'var(--text-primary)',
            border: multiSelectMode ? '1px solid var(--color-primary)' : '1px solid var(--border-strong)',
            borderRadius: 6,
            padding: '6px 8px',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
            <path d="M13 13l6 6" />
          </svg>
          {selectedNodeIds.length > 1 && (
            <span style={{ fontSize: 11, fontWeight: 600 }}>{selectedNodeIds.length}</span>
          )}
        </button>
      )}

      <div style={{ width: 1, height: 24, background: 'var(--bg-surface-raised)', margin: '0 4px' }} />

      <ToolbarButton label="+" commandId="view.zoomIn" shortcut="Ctrl+=" compact={isMobile} />
      <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontFamily: 'var(--font-family)' }}>
        {Math.round(zoom * 100)}%
      </span>
      <ToolbarButton label="-" commandId="view.zoomOut" shortcut="Ctrl+-" compact={isMobile} />
      <ToolbarButton
        label={
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        }
        commandId="view.fitToScreen"
        shortcut="Ctrl+0"
        compact={isMobile}
      />

      {!isMobile && (
        <>
          <div style={{ width: 1, height: 24, background: 'var(--bg-surface-raised)', margin: '0 4px' }} />
          <LayoutPicker />
        </>
      )}

      <div style={{ flex: 1 }} />

      {!isMobile && isAuthenticated && mapId && (
        <button
          onClick={() => setShareOpen(true)}
          title={t('toolbar.shareMap')}
          style={{
            background: 'var(--bg-surface-raised)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            padding: '6px 8px',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </button>
      )}
      {!isMobile && onImported && <ImportDialog onImported={onImported} />}
      {!isMobile && <ExportMenu />}

      {!isMobile && isAuthenticated && onOpenPricing && (
        <button
          onClick={onOpenPricing}
          title={t('toolbar.viewPlans')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: currentPlan === 'free' ? 'var(--bg-surface-raised)' : 'var(--bg-active)',
            color: currentPlan === 'free' ? 'var(--text-secondary)' : 'var(--text-link)',
            border: '1px solid ' + (currentPlan === 'free' ? 'var(--border-strong)' : 'var(--color-primary)'),
            borderRadius: 'var(--radius-lg)',
            padding: '2px 10px',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            fontFamily: 'var(--font-family)',
            textTransform: 'capitalize',
            marginLeft: 8,
            cursor: 'pointer',
          }}
        >
          {currentPlan}
        </button>
      )}

      {!isMobile && isAuthenticated && currentPlan === 'free' && onOpenPricing && (
        <button
          onClick={onOpenPricing}
          title={t('toolbar.upgradePlan')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-link)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            padding: '2px 4px',
          }}
        >
          {t('toolbar.upgrade')}
        </button>
      )}

      {/* Desktop-only: theme, language, profile, sign out */}
      {!isMobile && (
        <>
          <button
            onClick={toggleAppTheme}
            title={appTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            style={{
              background: 'var(--bg-surface-raised)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-strong)',
              borderRadius: '50%',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {appTheme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>

          <button
            onClick={() => {
              const next = i18n.language === 'en' ? 'de' : 'en';
              void i18n.changeLanguage(next);
              localStorage.setItem('app-language', next);
            }}
            title={i18n.language === 'en' ? 'Sprache: Deutsch' : 'Language: English'}
            style={{
              background: 'var(--bg-surface-raised)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-strong)',
              borderRadius: '50%',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 700,
              fontFamily: 'var(--font-family)',
              padding: 0,
              textTransform: 'uppercase',
            }}
          >
            {i18n.language === 'en' ? 'DE' : 'EN'}
          </button>

          {isAuthenticated && authUser && (
            <button
              onClick={onOpenAccountSettings}
              title={t('toolbar.accountSettings')}
              style={{
                background: 'var(--bg-surface-raised)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'var(--font-family)',
                padding: 0,
              }}
            >
              {(authUser.name || authUser.email || '?')[0]!.toUpperCase()}
            </button>
          )}

          <button
            onClick={logout}
            title={t('toolbar.signOut')}
            style={{
              background: 'var(--bg-surface-raised)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family)',
            }}
          >
            {t('toolbar.signOut')}
          </button>
        </>
      )}

      {/* Mobile hamburger menu — all overflow items in one place */}
      {isMobile && (
        <div style={{ position: 'relative' }} ref={mobileMenuRef}>
          <button
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            title="Menu"
            style={{
              background: 'var(--bg-surface-raised)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          {mobileMenuOpen && (
            <>
              <div
                onClick={closeMobileMenu}
                style={{ position: 'fixed', inset: 0, zIndex: 99 }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 8,
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 100,
                  minWidth: 200,
                  padding: '4px 0',
                  fontFamily: 'var(--font-family)',
                }}
              >
                {/* Order matches desktop: Share, Import, Export, Layout, Plan, Theme, Language, Profile, Sign Out */}

                {/* Share */}
                {isAuthenticated && mapId && (
                  <MenuItem onClick={() => { closeMobileMenu(); setShareOpen(true); }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                      {t('toolbar.share')}
                    </span>
                  </MenuItem>
                )}

                {/* Import */}
                {onImported && (
                  <div
                    style={{ borderTop: '1px solid var(--border-subtle)', padding: '0 4px' }}
                    onClick={closeMobileMenu}
                  >
                    <ImportDialog onImported={onImported} />
                  </div>
                )}

                {/* Export */}
                <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '0 4px' }}>
                  <ExportMenu />
                </div>

                {/* Layout picker */}
                <div style={{ padding: '6px 12px', borderTop: '1px solid var(--border-subtle)' }}>
                  <LayoutPicker />
                </div>

                {/* Plan */}
                {isAuthenticated && onOpenPricing && (
                  <MenuItem onClick={() => { closeMobileMenu(); onOpenPricing(); }} borderTop>
                    {currentPlan} plan{currentPlan === 'free' ? ' — Upgrade' : ''}
                  </MenuItem>
                )}

                {/* Theme toggle */}
                <MenuItem onClick={() => { closeMobileMenu(); toggleAppTheme(); }} borderTop>
                  {appTheme === 'dark' ? 'Light theme' : 'Dark theme'}
                </MenuItem>

                {/* Language toggle */}
                <MenuItem onClick={() => { closeMobileMenu(); const next = i18n.language === 'en' ? 'de' : 'en'; void i18n.changeLanguage(next); localStorage.setItem('app-language', next); }} borderTop>
                  {i18n.language === 'en' ? 'Deutsch' : 'English'}
                </MenuItem>

                {/* Profile / Account Settings */}
                {isAuthenticated && authUser && onOpenAccountSettings && (
                  <MenuItem onClick={() => { closeMobileMenu(); onOpenAccountSettings(); }} borderTop>
                    {t('toolbar.accountSettings')}
                  </MenuItem>
                )}

                {/* Sign out */}
                <MenuItem onClick={() => { closeMobileMenu(); logout(); }} borderTop>
                  {t('toolbar.signOut')}
                </MenuItem>
              </div>
            </>
          )}
        </div>
      )}

      {mapId && (
        <ShareDialog
          mapId={mapId}
          mapTitle={meta.title}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          onOpenPricing={onOpenPricing}
        />
      )}
    </div>
  );
}
